import * as moment from "moment";
import { connect, ConnectedComponent } from "react-redux";
import { selectDate, selectSlot, selectProvider } from "../../../Actions/availabilityActions";
import { Availability as DateTypeAvailability, ISlot, SlotType, IDay } from "../../../DataTypes/dateTypes";
import { IAvailabilityStepInfo, IStore } from "../../../DataTypes/storeTypes";
import { getAvailability, getShowingDateByInfo } from "../../../Helpers/calendarHelper";
import { Navigation } from "./Navigation";
import Loading from "../../../../Common/Loading";
import { getTimeZoneText } from "../../../../Common/timeZoneUtils";
import { flushSteps } from "../../../Actions/actions";
import { Step } from "../../../DataTypes/storeTypes";
import { Component, Fragment, SyntheticEvent } from "react";
import { Tooltip } from "@powerdiary/components-library/lib";

interface IProps {
    availability: DateTypeAvailability;
    info: IAvailabilityStepInfo;
    selectedProviderId?: number;
    selectedProviderName?: string;
    timeZoneText: string;

    selectDate(date: moment.Moment);
    selectSlot(slot: ISlot);
    selectProvider(providerId: number);
}

function PartOfDay({ slot, label }: { slot: boolean | ISlot[]; label: string }) {
    if (!!!slot) return null;

    return (
        <Tooltip title={label}>
            <li className={label.toLowerCase()}>
                <i className="fa fa-square" />
            </li>
        </Tooltip>
    );
}

const isDateBetween = (date: moment.Moment, start: moment.Moment | null, end: moment.Moment | null) => {
    let weekStart = start;
    let weekEnd = end;

    if (start === null) weekStart = moment(date).startOf("isoWeek"); // monday

    if (end === null) weekEnd = moment(date).endOf("isoWeek"); // sunday

    return date.isBetween(weekStart, weekEnd, null, "[]");
};

class Availability extends Component<IProps> {
    slotsForSelectedDay = (day: IDay) => {
        const availability = day.availability;

        if (!availability) return {};

        return {
            morningSlotsField: this.getPeriodSlots(availability.morning),
            afternoonSlotsField: this.getPeriodSlots(availability.afternoon),
            eveningSlotsField: this.getPeriodSlots(availability.evening),
        };
    };

    getPeriodSlots = (period: ISlot[] | boolean) => {
        if (!period) {
            return null;
        }

        const { info, selectedProviderId, selectProvider, selectedProviderName } = this.props;

        const result = (period as ISlot[]).map((slot) => {
            const isSlotSelected = slot.id === (info.slot ? info.slot.id : null);
            const providerName = selectedProviderId ? selectedProviderName : "";
            const slotName =
                slot.type === SlotType.Class
                    ? `${slot.date.format("h:mm a")} ${slot.name} ${providerName}`
                    : slot.date.format("h:mm a");
            const slotElem = (
                <div
                    className="heading"
                    onClick={(e) => {
                        e.stopPropagation();
                        this.props.selectSlot({ ...slot });
                    }}
                >
                    {slotName}
                </div>
            );

            if (!slot.providers || !isSlotSelected) {
                return (
                    <li className="label" key={slot.id}>
                        {slotElem}
                    </li>
                );
            }

            const providers = slot.providers.map((p) => (
                <option key={p.id} value={p.id}>
                    {p.name} {`${selectedProviderId === p.id ? "S" : ""}`}
                </option>
            ));

            return (
                <li className={`label ${isSlotSelected ? "active" : ""}`} key={slot.id}>
                    <div className="slot-container">
                        {slotElem}
                        <div className="provider-container">
                            <select
                                className="form-control"
                                onChange={(e: SyntheticEvent<HTMLSelectElement>) =>
                                    selectProvider(+e.currentTarget.value)
                                }
                            >
                                <option value="">Select provider</option>
                                {providers}
                            </select>
                        </div>
                    </div>
                </li>
            );
        });

        return result;
    };

    render() {
        if (!this.props.availability) {
            return null;
        }

        const { info } = this.props;

        const date = getShowingDateByInfo(info);
        const weeks: IDay[][] = getAvailability(date, this.props.availability);
        const month = date.format("MM");

        let morningSlots = null;
        let afternoonSlots = null;
        let eveningSlots = null;

        const grid = weeks
            ? weeks.map((week, weekIndex) => {
                  const days = week.map((day, i) => {
                      if (!day.availability)
                          return (
                              <div key={i} className="cal-cell">
                                  <span>&nbsp;</span>
                              </div>
                          );

                      const isSelected = day.date.isSame(info.selectedDate);

                      if (isSelected) {
                          const { morningSlotsField, afternoonSlotsField, eveningSlotsField } =
                              this.slotsForSelectedDay(day);
                          morningSlots = morningSlotsField;
                          afternoonSlots = afternoonSlotsField;
                          eveningSlots = eveningSlotsField;
                      }

                      const { morning, evening, afternoon } = day.availability;

                      const clickable = afternoon || morning || evening;

                      return (
                          <div
                              key={i}
                              className={`cal-cell available ${clickable ? "clickable" : ""} ${isSelected ? "selected" : ""}`}
                              onClick={() => {
                                  this.props.selectDate(isSelected ? null : day.date);
                              }}
                          >
                              <span>{`${day.date.format("D")} `}</span>

                              <ul className="list-inline">
                                  <PartOfDay slot={morning} label="Morning" />
                                  <PartOfDay slot={afternoon} label="Afternoon" />
                                  <PartOfDay slot={evening} label="Evening" />
                              </ul>
                          </div>
                      );
                  });

                  const { selectedDate } = info;

                  const isSelectedWeek =
                      selectedDate &&
                      isDateBetween(selectedDate, week[0].date, week[week.length - 1].date) &&
                      selectedDate.format("MM") === month;

                  return (
                      <Fragment key={weekIndex}>
                          <div className="cal-row">{days}</div>
                          <div
                              key={`slide-${month}-${weekIndex}`}
                              className={`cal-slide-box ${month} ${isSelectedWeek ? "" : "d-none"}`}
                          >
                              {isSelectedWeek ? (
                                  <>
                                      {morningSlots && <ul className="list-inline morning">{morningSlots}</ul>}
                                      {afternoonSlots && <ul className="list-inline afternoon">{afternoonSlots}</ul>}
                                      {eveningSlots && <ul className="list-inline evening">{eveningSlots}</ul>}
                                  </>
                              ) : null}
                          </div>
                      </Fragment>
                  );
              })
            : null;

        return (
            <>
                <div className="row">
                    <div className="col-lg-11 col-12 ml-auto mr-auto">
                        <div className="row align-items-center mb-2 small font-weight-bold">
                            <div className="col-md-7">
                                <div className="cal-legend">
                                    <ul className="list-inline">
                                        <li className="morning">
                                            <i className="fa fa-square"></i>Morning
                                        </li>
                                        <li className="afternoon">
                                            <i className="fa fa-square"></i>Afternoon
                                        </li>
                                        <li className="evening">
                                            <i className="fa fa-square"></i>Evening
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div className="col-md-5 text-right">{this.props.timeZoneText}</div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-lg-11 col-12 ml-auto mr-auto">
                        <div id="calendar">
                            <div className="cal-caption">
                                <Navigation />
                            </div>
                            <div className="cal-row-head">
                                <div className="cal-cell">M</div>
                                <div className="cal-cell">T</div>
                                <div className="cal-cell">W</div>
                                <div className="cal-cell">T</div>
                                <div className="cal-cell">F</div>
                                <div className="cal-cell">S</div>
                                <div className="cal-cell">S</div>
                            </div>
                            {grid ? (
                                grid.length ? (
                                    grid
                                ) : (
                                    <div className="mt-3">No Appointments Available</div>
                                )
                            ) : (
                                <Loading />
                            )}
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

const mapStateToProps = (state: IStore, ownProps: IProps): IProps => {
    const info = state.stepsInfo[state.common.currentStep] as IAvailabilityStepInfo;

    const providerInfo = state.stepsInfo[Step.Provider];

    return {
        ...ownProps,
        availability: state.availability,
        selectedProviderId: providerInfo.id,
        selectedProviderName: providerInfo.summary,
        info: { ...info },
        timeZoneText: getTimeZoneText(),
    };
};

const mapDispatchToProps = (dispatch, ownProps: IProps): IProps => {
    return {
        ...ownProps,
        selectDate: (date: moment.Moment) => {
            dispatch(flushSteps([Step.Group]));
            dispatch(selectDate(date));
        },
        selectProvider: (providerId: number) => dispatch(selectProvider(providerId)),
        selectSlot: (slot: ISlot) => dispatch(selectSlot(slot)),
    };
};

export const AvailabilityConnected: ConnectedComponent<typeof Availability, any> = connect(
    mapStateToProps,
    mapDispatchToProps
)(Availability);
